import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  listenToRide, updateRideStatus, getRideFresh, cancelRideAsDriver, startRideWithOtp,
  reportRideAnomaly, prewarmDriverUpdateRide, prewarmStartRide,
} from '../services/firebase';
import { useLanguage } from '../i18n/LanguageContext';
import { useDriver } from '../context/DriverContext';
import { openNavigation, getDriverUpiPayUrl } from '../config/app';
import ChatPanel from '../components/ChatPanel';
import CancelReasonModal from '../components/CancelReasonModal';
import DriverSosSheet from '../components/DriverSosSheet';
import TripProgress from '../components/TripProgress';
import TripOtpInput from '../components/TripOtpInput';
import QrCodeView from '../components/QrCodeView';
import {
  createMap, getDirections, drawRoute, placeMarker, placeSelfMarker, updateSelfMarker,
  fitMapToPoints, clearMarkers, getCurrentPosition, watchPosition, distanceBetweenM,
} from '../services/maps';
import { buildTelUrl, buildWhatsappUrl } from '../utils/support';
import { showFloatingBubble, hideFloatingBubble } from '../utils/floatingBubble';
import { playAlertSound } from '../utils/alertSound';
import {
  startNativeTripTrail,
  setNativeTripLeg,
  getNativeTripTrail,
  stopNativeTripTrail,
} from '../utils/backgroundLocation';
import { queuePlayReviewForCompletedTrip } from '../utils/playReview';
import { hapticSelection, hapticSuccess } from '../utils/haptics';

const ACTIVE_STATUSES = ['driver_assigned', 'arrived', 'started'];

const STATUS_RANK = {
  searching: 0,
  driver_assigned: 1,
  arrived: 2,
  started: 3,
  completed: 4,
  cancelled: 99,
};

const mergeTrails = (nativeList, jsList) => {
  const map = new Map();
  (nativeList || []).forEach((pt) => {
    if (pt?.lat && pt?.lng) {
      const key = `${Number(pt.lat).toFixed(5)},${Number(pt.lng).toFixed(5)},${Math.round((pt.t || 0) / 1000)}`;
      map.set(key, pt);
    }
  });
  (jsList || []).forEach((pt) => {
    if (pt?.lat && pt?.lng) {
      const key = `${Number(pt.lat).toFixed(5)},${Number(pt.lng).toFixed(5)},${Math.round((pt.t || 0) / 1000)}`;
      if (!map.has(key)) {
        map.set(key, pt);
      }
    }
  });
  const merged = Array.from(map.values());
  merged.sort((a, b) => (a.t || 0) - (b.t || 0));
  return merged;
};

export default function ActiveTrip() {
  const { rideId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user, driver, noteRideEnded, myPos } = useDriver();
  const [ride, setRide] = useState(() => location.state?.optimisticRide || null);
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [confirmCash, setConfirmCash] = useState(false);
  const [showProblem, setShowProblem] = useState(false);
  const [problemNote, setProblemNote] = useState('');
  const [showSos, setShowSos] = useState(false);

  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markers = useRef([]);
  const selfMarker = useRef(null);
  const selfMarkerBusy = useRef(false);
  const routeLine = useRef(null);
  const lastRouteKeyRef = useRef('');
  const mapReady = useRef(false);
  const completedOnce = useRef(false);
  // Synchronous re-entrancy guard for trip action buttons — `busy` is async
  // state, so two taps in the same frame both see it false and double-fire (L1).
  const actionLockRef = useRef(false);
  const isStartingRef = useRef(false);
  const arrivedPromiseRef = useRef(null);
  // Set the instant the server confirms arrival — not read from `ride.status`,
  // which is set optimistically before the network call even starts and so
  // can't tell handleStart "the server actually confirmed this" on its own.
  const arrivalConfirmedRef = useRef(false);
  const hasPrewarmedPickupRef = useRef(false);
  const hasPrewarmedDropRef = useRef(false);
  const navigateRef = useRef(navigate);
  const tRef = useRef(t);
  const rideRef = useRef(null);
  const sheetRef = useRef(null);
  const trailRef = useRef([]);
  const noteRideEndedRef = useRef(noteRideEnded);

  useEffect(() => { navigateRef.current = navigate; }, [navigate]);
  useEffect(() => { tRef.current = t; }, [t]);
  useEffect(() => { rideRef.current = ride; }, [ride]);
  useEffect(() => { noteRideEndedRef.current = noteRideEnded; }, [noteRideEnded]);

  // Tell the context this ride is over *before* navigating home, so the
  // Dashboard's "if (activeRide) -> /trip" effect can't bounce us back while the
  // ride listeners are still catching up (M4).
  const leaveTrip = useCallback(() => {
    try { noteRideEndedRef.current?.(rideId); } catch { /* */ }
    navigateRef.current('/', { replace: true });
  }, [rideId]);

  const applyRide = useCallback((data) => {
    if (!data) {
      setRide(null);
      hideFloatingBubble();
      leaveTrip();
      return;
    }

    const activeUid = user?.uid;
    if (data.driverId && activeUid && data.driverId !== activeUid) {
      setRide(null);
      hideFloatingBubble();
      toast.error(tRef.current('dash.rideTaken') || 'Ride was assigned to another Anna');
      leaveTrip();
      return;
    }

    // A synced snapshot is proof the server confirmed this status — unlike the
    // optimistic setRide in handleArrived, which fires before its network call
    // even starts. Covers the case where the app process was killed and
    // restarted while arrived/started: arrivalConfirmedRef resets to false on
    // remount, but the very next snapshot here re-derives it correctly.
    if (data.status === 'arrived' || data.status === 'started' || data.status === 'completed') {
      arrivalConfirmedRef.current = true;
    }

    if (data.status === 'cancelled') {
      setRide(data);
      hideFloatingBubble();
      playAlertSound();
      const cancelMsg = data.cancelledBy === 'passenger'
        ? (tRef.current('trip.passengerCancelled') || 'Passenger cancelled this ride')
        : (tRef.current('trip.rideCancelled') || 'Ride was cancelled');
      toast.error(cancelMsg, { duration: 5000, icon: '🚫' });
      leaveTrip();
      return;
    }
    if (data.status === 'completed') {
      setRide(data);
      hideFloatingBubble();
      if (!completedOnce.current) {
        completedOnce.current = true;
        toast.success(tRef.current('trip.completedToast'));
        noteRideEndedRef.current?.(rideId);
        setTimeout(() => navigateRef.current('/', { replace: true }), 350);
      }
      return;
    }

    // Monotonic guard: do not allow delayed server snapshots to roll back an active local transition
    const currentStatus = rideRef.current?.status;
    if (
      currentStatus &&
      STATUS_RANK[currentStatus] != null &&
      STATUS_RANK[data.status] != null &&
      STATUS_RANK[currentStatus] > STATUS_RANK[data.status]
    ) {
      setRide((prev) => (prev ? { ...data, status: prev.status } : data));
      return;
    }

    setRide(data);
  }, [user?.uid, leaveTrip, rideId]);

  const refreshRide = useCallback(async () => {
    if (!rideId) return;
    try {
      const fresh = await getRideFresh(rideId);
      if (!fresh) return;
      const prev = rideRef.current;
      if (!prev || prev.status !== fresh.status) applyRide(fresh);
      else setRide(fresh);
    } catch (e) {
      console.error('refreshRide:', e);
    }
  }, [rideId, applyRide]);

  useEffect(() => {
    if (!rideId) return undefined;
    // Live listener only — skip blocking getDocFromServer on open (felt laggy).
    return listenToRide(rideId, applyRide, () => refreshRide());
  }, [rideId, applyRide, refreshRide]);

  useEffect(() => {
    if (!rideId) return undefined;
    const initialLeg = rideRef.current?.status === 'started' ? 'trip' : 'pickup';
    startNativeTripTrail(rideId, initialLeg);
  }, [rideId]);

  useEffect(() => {
    if (!ride?.status) return;
    if (ride.status === 'started') {
      setNativeTripLeg('trip');
    } else if (ride.status === 'driver_assigned' || ride.status === 'arrived') {
      setNativeTripLeg('pickup');
    }
  }, [ride?.status]);

  useEffect(() => {
    if (!rideId) return undefined;
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        hideFloatingBubble();
        refreshRide();
      } else if (document.visibilityState === 'hidden' && rideRef.current && ACTIVE_STATUSES.includes(rideRef.current.status)) {
        showFloatingBubble();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      hideFloatingBubble();
    };
  }, [rideId, refreshRide]);

  const ensureSelfMarker = useCallback(async (pos) => {
    if (!mapInstance.current || !pos) return;
    if (selfMarker.current) {
      updateSelfMarker(selfMarker.current, pos);
      return;
    }
    if (selfMarkerBusy.current) return;
    selfMarkerBusy.current = true;
    try {
      selfMarker.current = await placeSelfMarker(mapInstance.current, pos);
    } catch (e) {
      console.warn('self marker', e);
    } finally {
      selfMarkerBusy.current = false;
    }
  }, []);

  const sheetBottomPad = () => {
    const h = sheetRef.current?.offsetHeight || 280;
    return Math.min(Math.max(h + 16, 220), 420);
  };

  const lastPosRef = useRef(null);

  const refreshRoute = useCallback(async (rideData, customPos = null) => {
    if (!mapInstance.current || !rideData) return;
    const pickup = rideData.pickup;
    const drop = rideData.destination;
    if (!pickup?.lat || !pickup?.lng) return;

    clearMarkers(markers.current);
    if (routeLine.current) {
      try { routeLine.current.setMap(null); } catch { /* */ }
      routeLine.current = null;
    }

    let pos = customPos || lastPosRef.current;
    if (!pos) {
      getCurrentPosition({ maximumAge: 10000, timeout: 4000 })
        .then((freshPos) => {
          lastPosRef.current = freshPos;
          ensureSelfMarker(freshPos);
        })
        .catch(() => {});
    } else {
      ensureSelfMarker(pos);
    }

    // Always place Pickup Pin (A)
    try {
      const mA = await placeMarker(mapInstance.current, pickup, 'A', '#00A86B');
      if (mA) markers.current.push(mA);
    } catch (e) {
      console.warn('marker A failed', e);
    }

    // Always place Drop Pin (B) if destination exists
    if (drop?.lat && drop?.lng) {
      try {
        const mB = await placeMarker(mapInstance.current, drop, 'B', '#FF6B35');
        if (mB) markers.current.push(mB);
      } catch (e) {
        console.warn('marker B failed', e);
      }
    }

    // Determine navigation segment:
    // Phase 1 (driver_assigned / arrived): Route from Anna -> Pickup (or Pickup -> Drop if already at pickup)
    // Phase 2 (started): Route from Anna -> Drop
    let origin = pos && pos.lat ? pos : pickup;
    let dest = rideData.status === 'started' ? drop : pickup;

    if (rideData.status !== 'started' && pos && pickup) {
      const distToPickup = distanceBetweenM(pos, pickup);
      if (distToPickup < 25 && drop?.lat) {
        // Already at pickup point — preview route to drop
        dest = drop;
      }
    }

    if (origin?.lat && dest?.lat) {
      getDirections(origin, dest).then(async (info) => {
        if (info?.polyline && mapInstance.current) {
          if (routeLine.current) {
            try { routeLine.current.setMap(null); } catch { /* */ }
          }
          routeLine.current = await drawRoute(mapInstance.current, info.polyline);
        }
      }).catch(() => {});
    }

    // Frame map to show Anna, Pickup, and Drop without over-zooming
    const fitPts = [];
    if (pos?.lat) fitPts.push(pos);
    if (pickup?.lat) fitPts.push(pickup);
    if (drop?.lat) fitPts.push(drop);

    if (fitPts.length && mapInstance.current) {
      fitMapToPoints(mapInstance.current, fitPts, {
        top: 60,
        right: 40,
        left: 40,
        bottom: sheetBottomPad(),
      }, 15.5);
    }
  }, [ensureSelfMarker]);

  useEffect(() => {
    if (!ride?.pickup || mapReady.current) return undefined;
    let cancelled = false;
    (async () => {
      try {
        if (!mapRef.current) return;
        const map = await createMap(mapRef.current, ride.pickup);
        if (cancelled) return;
        mapInstance.current = map;
        mapReady.current = true;
        await refreshRoute(ride);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => { cancelled = true; };
  }, [ride?.pickup, refreshRoute, ride]);

  useEffect(() => {
    if (!ride || !mapReady.current) return undefined;
    const handle = requestAnimationFrame(() => {
      refreshRoute(ride);
    });
    return () => cancelAnimationFrame(handle);
  }, [ride?.status, refreshRoute]);

  // Live Anna location while on an active trip.
  useEffect(() => {
    if (!ride || !ACTIVE_STATUSES.includes(ride.status)) return undefined;

    const applyPos = (pos) => {
      lastPosRef.current = pos;
      ensureSelfMarker(pos);
      if (!routeLine.current && rideRef.current) {
        refreshRoute(rideRef.current, pos);
      }
      const currentStatus = rideRef.current?.status;
      const isTrackingLeg = ['driver_assigned', 'started'].includes(currentStatus);
      if (isTrackingLeg && pos?.lat && pos?.lng) {
        // High-Precision GPS Quality Filter: Reject coarse cell-tower fixes (>25m error)
        const accuracy = Number(pos.accuracy) || 0;
        if (accuracy > 25) {
          return;
        }

        const trail = trailRef.current;
        const last = trail[trail.length - 1];
        const now = Date.now();

        // Teleport Guard: Discard impossible GPS jumps (>70m in <2s or >70 km/h)
        if (last) {
          const distM = distanceBetweenM(last, pos);
          const dtSec = Math.max(0.5, (now - (last.t || 0)) / 1000);
          const speedKmh = (distM / dtSec) * 3.6;
          if (distM > 70 && speedKmh > 70) {
            return;
          }
        }

        const leg = currentStatus === 'driver_assigned' ? 'pickup' : 'trip';

        // High-resolution recording: record every 3 meters for smooth road curves
        if (!last || distanceBetweenM(last, pos) >= 3 || (now - (last.t || 0) >= 4000) || last.leg !== leg) {
          trail.push({
            lat: Number(Number(pos.lat).toFixed(6)),
            lng: Number(Number(pos.lng).toFixed(6)),
            t: now,
            spd: Number(Number(pos.speed || 0).toFixed(1)),
            acc: Math.round(accuracy),
            leg,
          });
        }

        // Speed Anomaly Monitoring (>50 km/h in town)
        const speedKmh = pos.speed != null && pos.speed > 0
          ? Math.round(pos.speed * 3.6)
          : (last && now - (last.t || 0) > 0 ? Math.round((distanceBetweenM(last, pos) / ((now - last.t) / 1000)) * 3.6) : 0);
        if (speedKmh > 50 && speedKmh < 130) {
          reportRideAnomaly({
            rideId,
            type: 'overspeed',
            speedKmh,
            lat: pos.lat,
            lng: pos.lng,
          });
        }

        // Approach pre-warm pings: wake up Cloud Run instances before Anna reaches pickup / destination
        const currentRide = rideRef.current;
        if (currentRide?.status === 'driver_assigned' && currentRide.pickup?.lat && !hasPrewarmedPickupRef.current) {
          const distToPickup = distanceBetweenM(pos, currentRide.pickup);
          if (distToPickup <= 250) {
            hasPrewarmedPickupRef.current = true;
            prewarmDriverUpdateRide();
          }
        } else if (currentRide?.status === 'started' && currentRide.destination?.lat && !hasPrewarmedDropRef.current) {
          const distToDrop = distanceBetweenM(pos, currentRide.destination);
          if (distToDrop <= 250) {
            hasPrewarmedDropRef.current = true;
            prewarmDriverUpdateRide();
          }
        }
      }
    };

    const watchId = watchPosition(applyPos, () => {}, { minMs: 1500, minMeters: 4 });

    const retry = () => {
      getCurrentPosition({ maximumAge: 0, timeout: 20000 })
        .then(applyPos)
        .catch(() => {});
    };
    window.addEventListener('mt_location_ready', retry);
    const onVisible = () => {
      if (document.visibilityState === 'visible') retry();
    };
    document.addEventListener('visibilitychange', onVisible);
    const t1 = setTimeout(retry, 600);
    const t2 = setTimeout(retry, 2000);
    const t3 = setTimeout(retry, 5000);

    return () => {
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
      window.removeEventListener('mt_location_ready', retry);
      document.removeEventListener('visibilitychange', onVisible);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [ride?.status, rideId, ensureSelfMarker]);

  const navTarget = () => {
    if (!ride) return null;
    if (ride.status === 'started') return ride.destination;
    return ride.pickup;
  };

  const handleArrived = async () => {
    if (actionLockRef.current || busy) return;
    actionLockRef.current = true;
    // Distinct from isVerifyingOtp (which only guards the OTP input/Start
    // button) — this is the Arrived button's own re-entrancy guard. It must
    // stay held for the whole call, not just this synchronous tick, or a
    // second tap fires a second updateRideStatus before the first resolves.
    setBusy(true);
    hapticSelection();
    const prev = rideRef.current;
    // Optimistic UI — unfreeze and show OTP input immediately
    setRide((r) => (r ? { ...r, status: 'arrived' } : r));
    // Pre-warm the startRide Cloud Run container while the driver asks the passenger for the OTP
    prewarmStartRide();

    const arrivedPromise = updateRideStatus(rideId, 'arrived');
    // Set before anything awaits, so handleStart can pick it up immediately
    // even if the driver types the OTP within the same tick this call starts.
    arrivedPromiseRef.current = arrivedPromise;

    try {
      await arrivedPromise;
      // The server transaction committed — this is the one moment we can be
      // sure the ride is genuinely 'arrived', unlike the optimistic setRide
      // above which fired before the call even started.
      arrivalConfirmedRef.current = true;
    } catch (e) {
      const reason = e?.details?.reason;
      // Server says we're already at/past 'arrived' — nothing to roll back,
      // and arrival is confirmed true either way (just via an earlier call).
      // (Don't check rideRef.current here: the optimistic setRide above already
      //  flipped it to 'arrived', so it would swallow real failures.)
      if (reason === 'bad_transition') {
        arrivalConfirmedRef.current = true;
        return;
      }
      if (prev) setRide(prev);
      if (reason === 'too_far_from_pickup') {
        toast.error(t('trip.tooFarPickup') || 'Move closer to the pickup spot to mark arrived (within 250m).');
      } else {
        toast.error(t('authErrors.default'));
      }
    } finally {
      if (arrivedPromiseRef.current === arrivedPromise) {
        arrivedPromiseRef.current = null;
      }
      setBusy(false);
      actionLockRef.current = false;
    }
  };

  const handleStart = async (code) => {
    const entered = String(code || otp).replace(/\D/g, '').slice(0, 4);
    if (!entered || entered.length < 4) {
      toast.error(t('trip.otpRequired'));
      return;
    }
    if (isStartingRef.current || isVerifyingOtp) return;
    isStartingRef.current = true;
    setIsVerifyingOtp(true);
    setBusy(true);

    try {
      // If arrival network call is still in flight, wait for it before starting trip
      if (arrivedPromiseRef.current) {
        try {
          await arrivedPromiseRef.current;
        } catch (e) {
          // bad_transition means the ride was already past 'arrived' by the
          // time this call landed (arrival confirmed via an earlier call) —
          // same as handleArrived's own catch treats it. Anything else
          // (e.g. too_far_from_pickup) is a real failure — abort start.
          if (e?.details?.reason !== 'bad_transition') return;
        }
      }

      // If arrival was never actually confirmed by the server, abort. Checked
      // against the explicit ref set at confirmation time, not ride.status —
      // that's set optimistically before the network call even starts, so it
      // can't distinguish "confirmed" from "not yet confirmed".
      if (!arrivalConfirmedRef.current) {
        return;
      }

      const prev = rideRef.current;
      setRide((r) => (r ? { ...r, status: 'started' } : r));

      try {
        await startRideWithOtp(rideId, entered);
        setOtp('');
        hapticSuccess();
      } catch (e) {
        // startRideSecure RESOLVES (no throw) when the ride is already 'started',
        // so any error here is a real failure. Roll back to the OTP screen and
        // tell the driver — never advance to the payment step on a failed confirm.
        if (prev) setRide(prev);
        const reason = e?.details?.reason;
        const code = String(e?.code || '');
        if (reason === 'wrong_otp') {
          setOtp('');
          toast.error(t('trip.otpWrong'));
        } else if (code.includes('resource-exhausted')) {
          toast.error(t('trip.otpRateLimit'));
        } else if (reason === 'otp_expired') {
          setOtp('');
          toast.error(t('trip.otpExpired'));
        } else if (reason === 'bad_status') {
          toast.error(t('trip.otpStatusError'));
        } else {
          // network / App Check / unavailable — recoverable; keep the OTP entered
          toast.error(t('trip.otpRetry'));
        }
      }
    } finally {
      setIsVerifyingOtp(false);
      isStartingRef.current = false;
      setBusy(false);
    }
  };

  const handleComplete = async () => {
    if (!confirmCash) {
      setConfirmCash(true);
      return;
    }
    if (actionLockRef.current || completedOnce.current || busy) return;
    actionLockRef.current = true;
    setBusy(true);
    try {
      let finalTrail = trailRef.current || [];
      try {
        const nativePoints = await Promise.race([
          getNativeTripTrail(),
          new Promise((resolve) => setTimeout(() => resolve([]), 250)),
        ]);
        if (nativePoints && nativePoints.length > 0) {
          finalTrail = mergeTrails(nativePoints, trailRef.current);
        }
      } catch (err) {
        console.warn('getNativeTripTrail non-fatal:', err?.message);
      }
      // Downsample trail if >300 points to keep payload ultra-lightweight over cellular
      if (finalTrail.length > 300) {
        const step = Math.ceil(finalTrail.length / 300);
        const sampled = [];
        for (let i = 0; i < finalTrail.length; i += step) {
          sampled.push(finalTrail[i]);
        }
        if (sampled[sampled.length - 1] !== finalTrail[finalTrail.length - 1]) {
          sampled.push(finalTrail[finalTrail.length - 1]);
        }
        finalTrail = sampled;
      }
      await updateRideStatus(rideId, 'completed', { trail: finalTrail });
      stopNativeTripTrail().catch(() => {});
      // Keep busy until applyRide navigates — avoids double-tap "already completed" toast.
      if (!completedOnce.current) {
        completedOnce.current = true;
        hapticSuccess();
        toast.success(t('trip.completedToast'));
        queuePlayReviewForCompletedTrip({
          uid: user?.uid,
          rideId,
          targetCompletedRides: Number(driver?.totalCompletedRides) + 1,
        });
        noteRideEndedRef.current?.(rideId);
        setTimeout(() => navigate('/', { replace: true }), 350);
      }
    } catch (e) {
      // Only a genuinely-completed ride counts as success. A rejected transition
      // (e.g. Complete tapped before the OTP was verified) must NOT be swallowed
      // as success — the old code matched "completed" inside the message
      // "Cannot go from arrived to completed" and navigated home on failure.
      if (rideRef.current?.status === 'completed') {
        if (!completedOnce.current) {
          completedOnce.current = true;
          toast.success(t('trip.completedToast'));
          queuePlayReviewForCompletedTrip({
            uid: user?.uid,
            rideId,
            targetCompletedRides: Number(driver?.totalCompletedRides) + 1,
          });
          noteRideEndedRef.current?.(rideId);
          setTimeout(() => navigate('/', { replace: true }), 350);
        }
        return;
      }
      console.error('complete ride failed:', e);
      const reason = e?.details?.reason;
      if (reason === 'bad_transition') {
        toast.error(t('trip.completeStatusError') || 'Start the trip before finishing it.');
      } else {
        toast.error(t('authErrors.default'));
      }
      setBusy(false);
      actionLockRef.current = false;
    }
  };

  // Escape hatch for a trip that can't be completed (passenger no-show / dispute)
  // and offers no Cancel — flags the ride for an admin to resolve and releases
  // the captain from the trip screen (L4).
  const handleReportProblem = async () => {
    if (actionLockRef.current || busy) return;
    actionLockRef.current = true;
    setBusy(true);
    try {
      await reportRideAnomaly({ rideId, type: 'stuck', note: problemNote.trim().slice(0, 200) });
      toast.success(t('trip.problemReported') || 'Reported. Our team will close this trip. Contact support if it is urgent.');
      setShowProblem(false);
      leaveTrip();
    } catch (e) {
      toast.error(t('authErrors.default'));
      setBusy(false);
      actionLockRef.current = false;
    }
  };

  const handleCancel = async (reason) => {
    if (actionLockRef.current || busy) return;
    actionLockRef.current = true;
    setBusy(true);
    try {
      stopNativeTripTrail().catch(() => {});
      await cancelRideAsDriver(rideId, reason);
      toast.success(t('cancel.done'));
      leaveTrip();
    } catch {
      toast.error(t('authErrors.default'));
    } finally {
      setBusy(false);
      actionLockRef.current = false;
      setShowCancel(false);
    }
  };

  if (!ride) return <div className="splash"><div className="splash-loader" /></div>;

  const target = navTarget();
  // Live distance from the Anna's own GPS to the current leg target, so the top
  // bar counts down as she drives instead of showing the static booked trip
  // distance (which read "2.1 km to pickup" even while parked on the pickup).
  const fmtDist = (m) => (m < 1000 ? `${Math.max(10, Math.round(m / 10) * 10)} m` : `${(m / 1000).toFixed(1)} km`);
  const legDistM = (myPos?.lat != null && target?.lat != null)
    ? distanceBetweenM(myPos, target)
    : null;
  const etaBarText = (() => {
    const onTrip = ride.status === 'started';
    if (legDistM == null) {
      return onTrip
        ? t('trip.distToDrop', { dist: ride.distance || '…' })
        : t('trip.distToPickup', { dist: ride.distance || '…' });
    }
    if (!onTrip && legDistM < 40) return t('trip.atPickupBanner');
    return onTrip
      ? t('trip.distToDrop', { dist: fmtDist(legDistM) })
      : t('trip.distToPickup', { dist: fmtDist(legDistM) });
  })();
  const headline = t(`trip.status_${ride.status}`);
  const canCancel = ['driver_assigned', 'arrived'].includes(ride.status);
  const sosMsg = `SOS MyTownly Rides — ride ${rideId} — ${ride.pickup?.address || ''} → ${ride.destination?.name || ''}`;
  const tel = buildTelUrl();
  const wa = buildWhatsappUrl(sosMsg);
  const compactOtp = ride.status === 'arrived';

  return (
    <div className="driver-screen">
      {target && (
        <div className="dash-top-eta-bar">
          <span className="dash-eta-icon">⏱️</span>
          <span className="dash-eta-text">{etaBarText}</span>
        </div>
      )}

      <div ref={mapRef} className="driver-map" />

      <div
        ref={sheetRef}
        className={`trip-sheet${compactOtp ? ' trip-sheet--otp' : ''}`}
      >
        <div className="trip-sheet-handle" aria-hidden="true" />
        <TripProgress status={ride.status} t={t} />

        <div className="dash-timeline-card">
          <div className={`dash-timeline-item ${['driver_assigned', 'arrived'].includes(ride.status) ? 'active' : ''}`}>
            <span className="dash-timeline-dot pickup" />
            <div className="dash-timeline-copy">
              <span className="dash-timeline-label">{t('trip.pickup')}</span>
              <span className="dash-timeline-address">{ride.pickup?.address || 'Pickup Location'}</span>
            </div>
          </div>
          <div className="dash-timeline-line" />
          <div className={`dash-timeline-item ${ride.status === 'started' ? 'active' : ''}`}>
            <span className="dash-timeline-dot drop" />
            <div className="dash-timeline-copy">
              <span className="dash-timeline-label">{t('trip.drop')}</span>
              <span className="dash-timeline-address">{ride.destination?.name || 'Drop Location'}</span>
            </div>
          </div>
        </div>

        <div className="dash-customer-hero">
          <div className="dash-customer-left">
            <div className="dash-customer-avatar">
              {ride.customerName ? ride.customerName.charAt(0).toUpperCase() : '👤'}
            </div>
            <div className="dash-customer-meta">
              <div className="dash-customer-name">{ride.customerName || ride.riderName || t('trip.customer')}</div>
              <div className="dash-cash-pill">
                💵 {t('trip.collectCash', { fare: ride.fare })}
                {Number(ride?.cancellationFee) > 0 && (
                  <span style={{ fontSize: '11px', color: '#fbbf24', marginLeft: 4, fontWeight: 700 }}>
                    {t('trip.pastFeePill', { fee: ride.cancellationFee })}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="dash-customer-right">
            {ride.customerPhone && (
              <a href={`tel:${ride.customerPhone}`} className="dash-circle-btn call" title={t('trip.call')}>
                📞
                <span>{t('trip.call')}</span>
              </a>
            )}
            <button type="button" className="dash-circle-btn chat" onClick={() => setShowChat(true)} title={t('trip.chat')}>
              💬
              <span>{t('trip.chat')}</span>
            </button>
          </div>
        </div>

        {target && (
          <button
            type="button"
            className="dash-nav-banner"
            onClick={() => {
              showFloatingBubble();
              openNavigation(target.lat, target.lng);
            }}
          >
            <span className="dash-nav-logo">🗺️</span>
            <span className="dash-nav-title">{t('trip.openGmaps')}</span>
            <span className="dash-nav-arrow">➔</span>
          </button>
        )}

        <div className="trip-actions">
          {ride.status === 'driver_assigned' && (
            <button type="button" className="btn btn-green dash-action-btn" disabled={busy} onClick={handleArrived}>
              {t('trip.arrived')}
            </button>
          )}

          {ride.status === 'arrived' && (
            <>
              <div className="trip-otp-block">
                <div className="trip-otp-label">{t('trip.otpLabel')}</div>
                <TripOtpInput
                  value={otp}
                  onChange={setOtp}
                  onComplete={handleStart}
                  disabled={isVerifyingOtp}
                  autoFocus
                  hint={t('trip.otpHint')}
                />
              </div>
              <button
                type="button"
                className="btn btn-green dash-action-btn"
                disabled={isVerifyingOtp || otp.length < 4}
                onClick={() => handleStart()}
              >
                {isVerifyingOtp ? (t('trip.verifying') || 'Verifying…') : t('trip.startTrip')}
              </button>
            </>
          )}

          {ride.status === 'started' && (
            <button type="button" className="btn btn-green dash-action-btn" disabled={busy} onClick={() => setConfirmCash(true)}>
              {t('trip.complete')}
            </button>
          )}
        </div>

        <div className="dash-bottom-safety-bar">
          <button type="button" className="dash-safety-link sos" onClick={() => setShowSos(true)}>
            🚨 {t('trip.sosShort')}
          </button>
          {canCancel && (
            <button type="button" className="dash-safety-link cancel" disabled={busy} onClick={() => setShowCancel(true)}>
              {t('trip.cancelShort')}
            </button>
          )}
          {ride.status === 'started' && (
            <button type="button" className="dash-safety-link cancel" disabled={busy} onClick={() => setShowProblem(true)}>
              {t('trip.reportProblem') || 'Report a problem'}
            </button>
          )}
        </div>
      </div>

      {/* Dedicated Full-Screen Payment Collection Overlay */}
      {confirmCash && ride && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 99999,
          background: 'linear-gradient(180deg, #091e14 0%, #03140d 100%)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: '24px 20px calc(24px + env(safe-area-inset-bottom, 16px))',
          boxSizing: 'border-box',
          color: '#ffffff',
          animation: 'fadeIn 0.2s ease-out',
        }}>
          {/* Top Header Bar */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <button
              type="button"
              onClick={() => setConfirmCash(false)}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                color: '#e2e8f0',
                padding: '8px 16px',
                borderRadius: '20px',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <span>✕</span> {t('trip.back')}
            </button>

            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              background: 'rgba(0, 214, 143, 0.15)',
              border: '1px solid rgba(0, 214, 143, 0.3)',
              color: '#00d68f',
              padding: '6px 14px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: 800,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}>
              <span>🛺</span> MyTownly Anna Pay
            </div>
          </div>

          {/* Center Main QR Standee Section */}
          <div style={{ textAlign: 'center', margin: 'auto 0' }}>
            <div style={{ fontSize: '13px', fontWeight: 800, color: '#94a3b8', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>
              {t('trip.collectPaymentHeader', { fare: ride.fare })}
            </div>
            <div style={{
              fontSize: '52px',
              fontWeight: 800,
              color: '#00d68f',
              fontFamily: 'Clash Display, sans-serif',
              lineHeight: 1.1,
              marginBottom: Number(ride?.cancellationFee) > 0 ? 8 : 20,
              textShadow: '0 2px 20px rgba(0, 214, 143, 0.4)',
            }}>
              ₹{ride.fare}
            </div>

            {Number(ride?.cancellationFee) > 0 && (
              <div style={{
                fontSize: '13px',
                fontWeight: 600,
                color: '#fbbf24',
                marginBottom: 16,
                background: 'rgba(251, 191, 36, 0.1)',
                border: '1px solid rgba(251, 191, 36, 0.25)',
                padding: '4px 12px',
                borderRadius: '8px',
                display: 'inline-block',
              }}>
                {t('trip.pastFeeBreakdown', { ride: ride.fare - ride.cancellationFee, fee: ride.cancellationFee })}
              </div>
            )}

            {(driver?.upiId || driver?.upiQrUrl) ? (
              <div style={{
                background: '#ffffff',
                borderRadius: '22px',
                padding: '16px',
                width: 'fit-content',
                margin: '0 auto 18px',
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.5)',
              }}>
                <QrCodeView
                  text={getDriverUpiPayUrl({ upiId: driver.upiId, name: driver.name, amount: ride.fare })}
                  imageUrl={driver?.upiQrUrl}
                  size={240}
                />
              </div>
            ) : (
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1.5px dashed rgba(255, 255, 255, 0.2)',
                borderRadius: '20px',
                padding: '36px 20px',
                maxWidth: 320,
                margin: '0 auto 20px',
              }}>
                <span style={{ fontSize: '48px', display: 'block', marginBottom: 12 }}>💵</span>
                <div style={{ fontSize: '17px', fontWeight: 700, color: '#f8fafc' }}>
                  {t('trip.collectConfirm', { fare: ride.fare })}
                </div>
              </div>
            )}

            {(driver?.upiId || driver?.upiQrUrl) && (
              <>
                <div style={{ fontSize: '15px', color: '#f8fafc', fontWeight: 700 }}>
                  {t('trip.scanQrHint')}
                </div>
                {driver?.upiId && (
                  <div style={{
                    display: 'inline-block',
                    marginTop: 8,
                    background: 'rgba(255, 255, 255, 0.08)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    padding: '6px 16px',
                    borderRadius: '12px',
                    color: '#94a3b8',
                    fontSize: '13px',
                    fontFamily: 'monospace',
                  }}>
                    UPI: <strong style={{ color: '#00d68f' }}>{driver.upiId}</strong>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Bottom Action Button */}
          <div>
            <button
              type="button"
              className="btn btn-green dash-action-btn"
              disabled={busy}
              onClick={handleComplete}
              style={{
                width: '100%',
                padding: '18px',
                fontSize: '17px',
                fontWeight: 800,
                borderRadius: '16px',
                boxShadow: '0 6px 24px rgba(0, 214, 143, 0.45)',
                margin: 0,
              }}
            >
              {busy
                ? t('trip.completing')
                : confirmCash
                  ? (t('trip.confirmComplete') || `👉 Confirm ₹${ride.fare} Received — Finish Trip`)
                  : t('trip.cashReceivedBtn', { fare: ride.fare })}
            </button>
          </div>
        </div>
      )}

      <ChatPanel
        open={showChat}
        onClose={() => setShowChat(false)}
        rideId={rideId}
        userId={user?.uid}
        role="driver"
        t={t}
      />

      {showCancel && (
        <CancelReasonModal
          t={t}
          busy={busy}
          onClose={() => setShowCancel(false)}
          onConfirm={handleCancel}
        />
      )}

      {showSos && (
        <DriverSosSheet t={t} rideId={rideId} onClose={() => setShowSos(false)} />
      )}

      {showProblem && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 99999, background: 'rgba(3,7,18,0.82)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
          onClick={() => !busy && setShowProblem(false)}
        >
          <div
            style={{ width: '100%', maxWidth: 460, background: '#0f172a', borderTopLeftRadius: 22, borderTopRightRadius: 22, padding: 24, color: '#e2e8f0' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 6px', fontSize: 18 }}>{t('trip.reportProblem') || 'Report a problem'}</h3>
            <p className="muted" style={{ fontSize: 13, lineHeight: 1.5, marginBottom: 14 }}>
              {t('trip.reportProblemHint')
                || 'Use this if the trip can’t be finished — passenger no-show or a dispute. Our team will close the trip. You can then go back and take other rides.'}
            </p>
            <textarea
              value={problemNote}
              onChange={(e) => setProblemNote(e.target.value.slice(0, 200))}
              placeholder={t('trip.reportProblemPlaceholder') || 'What happened? (optional)'}
              rows={3}
              style={{ width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 12, padding: 10, color: '#e2e8f0', fontSize: 14, resize: 'none' }}
            />
            <button
              type="button"
              className="btn btn-green dash-action-btn"
              style={{ width: '100%', marginTop: 14 }}
              disabled={busy}
              onClick={handleReportProblem}
            >
              {busy ? '…' : (t('trip.reportProblemSend') || 'Send report & leave trip')}
            </button>
            <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
              {tel !== '#' && (
                <a className="btn btn-ghost" style={{ flex: 1, textAlign: 'center' }} href={tel}>
                  {t('trip.callSupport') || 'Call support'}
                </a>
              )}
              {wa !== '#' && (
                <a className="btn btn-ghost" style={{ flex: 1, textAlign: 'center' }} href={wa} target="_blank" rel="noopener noreferrer">
                  WhatsApp
                </a>
              )}
            </div>
            <button
              type="button"
              className="btn btn-ghost"
              style={{ width: '100%', marginTop: 10 }}
              disabled={busy}
              onClick={() => setShowProblem(false)}
            >
              {t('common.close') || 'Close'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
